import React, { useEffect, useRef, useState } from 'react';
import { Canvas, Spinner } from 'datocms-react-ui';
import Select, { SingleValue } from 'react-select';
import { parseTripJson } from '../utils/parseTripJson';
import { TripClassSelectProps, SelectOption } from '../types';
import get from 'lodash/get';

const TripClassSelect: React.FC<TripClassSelectProps> = ({ ctx }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [tripClassOptions, setTripClassOptions] = useState<SelectOption[]>([]);
  const [selectedTripClass, setSelectedTripClass] = useState<SelectOption | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Ref so the fetch effect can read the current selection without it being a dep
  const selectedTripClassRef = useRef(selectedTripClass);
  useEffect(() => { selectedTripClassRef.current = selectedTripClass; }, [selectedTripClass]);

  // Retrieve the saved field name from the plugin parameters in the plugin config
  const BmsTripJsonFieldName = ctx.plugin.attributes.parameters.BmsTripJsonFieldName as string | undefined;

  // Load the initial trip class selection when this field's saved value changes
  useEffect(() => {
    const savedTripClassJson = get(ctx.formValues, ctx.fieldPath) as string || null;
    if (savedTripClassJson) {
      try {
        const parsedTripClass = JSON.parse(savedTripClassJson);
        if (parsedTripClass?.trip_class) {
          setSelectedTripClass({ label: parsedTripClass.trip_class, value: parsedTripClass.trip_class });
        }
      } catch (e) {
        console.error('[plugin:trip-class-selector] Failed to parse saved trip class JSON:', e);
      }
    }
  // Narrow to the specific field value so this only re-runs when the saved selection changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.formValues[ctx.fieldPath]]);

  // Fetch trip class options from the BmsTripJsonFieldName field and combine base_class and trip_classes
  useEffect(() => {
    const fetchTripClassOptions = async () => {
      setError(null);

      if (!BmsTripJsonFieldName || !(BmsTripJsonFieldName in ctx.formValues)) {
        setTripClassOptions([]);
        if (selectedTripClassRef.current !== null) {
          setSelectedTripClass(null);
          await ctx.setFieldValue(ctx.fieldPath, null);
        }
        setError(`Field "${BmsTripJsonFieldName}" not found on the page`);
        setLoading(false);
        return;
      }

      const tripJsonData = ctx.formValues[BmsTripJsonFieldName] as string;

      if (!tripJsonData) {
        setError('BMS trip JSON is empty');
        setTripClassOptions([]);
        if (selectedTripClassRef.current !== null) {
          setSelectedTripClass(null);
          await ctx.setFieldValue(ctx.fieldPath, null);
        }
        setLoading(false);
        return;
      }

      try {
        const parsedTripJson = parseTripJson(tripJsonData);

        let classes: SelectOption[] = [];

        // Combine trip_classes and base_class if both are present
        if (Array.isArray(parsedTripJson?.trip_classes) && parsedTripJson.trip_classes.length > 0) {
          const tripClasses = parsedTripJson.trip_classes.map((tripClass: any) => ({
            label: tripClass.name,
            value: tripClass.name,
          }));
          classes = [...tripClasses];
        }

        if (parsedTripJson?.base_class) {
          const baseClass = { label: parsedTripJson.base_class.name, value: parsedTripJson.base_class.name };
          classes = [baseClass, ...classes];
        }

        if (classes.length === 0) {
          setError('No trip classes or base class available');
        }

        setTripClassOptions(classes);

        // If the selected trip class is no longer valid, reset — read via ref to avoid making
        // selectedTripClass a dependency (which would re-trigger this effect after the reset)
        const current = selectedTripClassRef.current;
        if (current && !classes.some(option => option.value === current.value)) {
          setSelectedTripClass(null);
          await ctx.setFieldValue(ctx.fieldPath, null);
        }
      } catch (e) {
        console.error('[plugin:trip-class-selector] Failed to parse trip JSON:', e);
        setError('Failed to parse trip JSON');
      }
      setLoading(false);
    };

    fetchTripClassOptions();
  // Depend only on the source BMS trip JSON value, not on ctx or selectedTripClass
  // ctx intentionally omitted — it changes reference on every render and would cause an infinite loop
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.formValues[BmsTripJsonFieldName as string], BmsTripJsonFieldName]);

  // Handle the selection of a class from the dropdown
  const handleClassSelect = async (newValue: SingleValue<SelectOption>) => {
    setSelectedTripClass(newValue);
    await ctx.setFieldValue(ctx.fieldPath, JSON.stringify({ trip_class: newValue?.value || null }));
  };

  const selectCustomStyles = {
    menu: (provided: any) => ({
      ...provided,
      zIndex: 9999,
      position: 'relative',
    }),
    control: (provided: any) => ({
      ...provided,
      borderRadius: 0,
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#f0f0f0' : '#fff',
      color: '#000000',
      ':hover': {
        backgroundColor: '#eaeaea',
      },
    }),
  };

  return (
    <Canvas ctx={ctx}>
      {loading ? (
        <div style={{ marginTop: 8 }}>{"Loading trip classes..."} <Spinner size={24} /></div>
      ) : error ? (
        <div style={{ marginTop: 8, color: 'red' }}>{error}</div>
      ) : tripClassOptions.length === 0 ? (
        <div style={{ marginTop: 8, color: 'gray' }}>No available trip classes</div>
      ) : (
        <Select
          id="trip-class-select"
          name="trip-class-select"
          placeholder="Select a trip class..."
          options={tripClassOptions}
          value={selectedTripClass}
          onChange={handleClassSelect}
          isClearable
          styles={selectCustomStyles}
          noOptionsMessage={() => "No trip classes available"}
        />
      )}
    </Canvas>
  );
};

export default TripClassSelect;
